<template>
  <div class="warehouse-returns-page p-4">
    <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
      <h3 class="text-xl font-semibold text-blue-900 m-0">
        {{ $t('returns') }}
      </h3>
      <div class="flex flex-wrap items-center gap-2">
        <b-button
          type="is-primary"
          size="is-small"
          :disabled="isCreateMode"
          @click="onLoadReturn"
        >
          {{ $t('load_returns') }}
        </b-button>
        <b-button
          type="is-primary"
          size="is-small"
          :loading="isExporting"
          :disabled="isExporting || isCreateMode"
          @click="onExport"
        >
          {{ $t('export') }}
        </b-button>
      </div>
    </div>

    <div v-if="isCreateMode" class="p-4 border border-gray-300 rounded bg-white">
      <h4 class="text-base font-semibold text-blue-900 mb-3">{{ isEditingMode ? $t('edit_return') : $t('create_return') }}</h4>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
        <b-field :label="$t('return_number')">
          <b-input :value="form.return_number" placeholder="IDM-AAAA-NNNNN" disabled />
        </b-field>
        <b-field :label="$t('returned_by')">
          <b-input v-model.trim="form.returned_by" maxlength="255" :placeholder="$t('returned_by')" />
        </b-field>
        <b-field :label="$t('return_date')">
          <b-input v-model="form.return_date" type="date" />
        </b-field>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
        <b-field :label="$t('total_quantity_label')">
          <b-input :value="String(totalDraftMaterialsQuantity)" disabled />
        </b-field>
        <b-field :label="$t('description')" class="md:col-span-2">
          <b-input v-model.trim="form.description" maxlength="500" type="textarea" />
        </b-field>
      </div>

      <div class="mt-4 p-3 border border-gray-300 rounded">
        <h5 class="text-base font-semibold text-blue-900 mb-3">{{ $t('material_search') }}</h5>

        <div class="mb-3 max-w-md">
          <b-field :label="$t('subcontractor')">
            <b-select
              v-model="inventoryFilters.partner_id"
              expanded
              :loading="isPartnersLoading"
              :disabled="isPartnersLoading"
              :placeholder="$t('subcontractor')"
              @input="onPartnerChange"
            >
              <option :value="null">{{ $t('all') }}</option>
              <option v-for="partner in subcontractorOptions" :key="`pt-${partner.id}`" :value="partner.id">
                {{ partner.name || partner.code || `#${partner.id}` }}
              </option>
            </b-select>
          </b-field>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-4 gap-3">
          <b-field :label="$t('material')">
            <warehouse-catalog-autocomplete
              v-model="inventoryFilters.warehouse_material_id"
              :project-id="normalizedProjectId"
              endpoint="materials"
              response-key="materials"
              order-field="code"
              :placeholder="$t('material')"
              :formatter="formatMaterialOption"
              :disabled="isOtherFiltersDisabled"
              @input="onFiltersChange"
            />
          </b-field>

          <b-field :label="$t('provider')">
            <warehouse-catalog-autocomplete
              v-model="inventoryFilters.warehouse_provider_id"
              :project-id="normalizedProjectId"
              endpoint="providers"
              response-key="providers"
              order-field="name"
              :placeholder="$t('provider')"
              :formatter="formatProviderOption"
              :disabled="isOtherFiltersDisabled"
              @input="onFiltersChange"
            />
          </b-field>

          <b-field :label="$t('unit')">
            <b-select
              v-model="inventoryFilters.unit_id"
              expanded
              :loading="isCatalogsLoading"
              :disabled="isOtherFiltersDisabled"
              :placeholder="$t('unit')"
              @input="onFiltersChange"
            >
              <option :value="null">{{ $t('all') }}</option>
              <option v-for="u in unitOptions" :key="`u-${u.id}`" :value="u.id">
                {{ (u.code || u.name || '-') }}
              </option>
            </b-select>
          </b-field>

          <b-field :label="$t('search')">
            <b-input
              v-model.trim="inventoryFilters.search"
              :placeholder="$t('search')"
              :disabled="isOtherFiltersDisabled"
              @input="onFiltersChangeDebounced"
            />
          </b-field>
        </div>

        <div
          v-if="normalizedPartnerId"
          class="mt-3 p-2 rounded bg-blue-50 border border-blue-200 text-sm text-blue-900"
        >
          {{ $t('warehouse_return_multi_outbound_hint') }}
        </div>
        <div
          v-else
          class="mt-3 text-sm text-gray-500"
        >
          {{ $t('select_subcontractor_to_load_outbound') }}
        </div>

        <div class="mt-3 max-w-full overflow-x-auto warehouse-table-responsive">
          <b-loading :active="isInventoryListLoading" :is-full-page="false" />
          <b-table
            :data="inventoryListRows"
            :row-class="inventoryRowClass"
            striped
            bordered
            narrowed
            mobile-cards
            icon-pack="fas"
          >
            <b-table-column field="delivery_number" :label="$t('delivery_number')" width="130" v-slot="props">
              {{ props.row.delivery_number || '-' }}
            </b-table-column>
            <b-table-column field="unique_material_id" :label="$t('unique_material_id')" v-slot="props">
              {{ (props.row.inventory && props.row.inventory.unique_material_id) || '-' }}
            </b-table-column>
            <b-table-column field="description" :label="$t('description')" v-slot="props">
              {{ (props.row.inventory && props.row.inventory.description) || '-' }}
            </b-table-column>
            <b-table-column field="provider" :label="$t('provider')" v-slot="props">
              {{ (props.row.inventory && props.row.inventory.provider) || '-' }}
            </b-table-column>
            <b-table-column field="unit" :label="$t('unit')" width="90" v-slot="props">
              {{ (props.row.inventory && props.row.inventory.unit) || '-' }}
            </b-table-column>
            <b-table-column field="drawing_number" :label="$t('drawing_number')" v-slot="props">
              {{ (props.row.inventory && props.row.inventory.drawing_number) || '-' }}
            </b-table-column>
            <b-table-column field="tag_kks" :label="$t('tag_kks')" v-slot="props">
              {{ (props.row.inventory && props.row.inventory.tag_kks) || '-' }}
            </b-table-column>
            <b-table-column field="outbound_quantity" :label="$t('outgoing_quantity')" numeric width="140" v-slot="props">
              {{ props.row.outbound_quantity != null ? props.row.outbound_quantity : '-' }}
            </b-table-column>
            <b-table-column field="pending" :label="$t('pending_after_return')" numeric width="160" v-slot="props">
              <span :class="pendingCellClass(props.row)">
                {{ pendingForRow(props.row) }}
              </span>
            </b-table-column>
            <b-table-column field="draft_qty" :label="$t('quantity')" numeric width="130" v-slot="props">
              <div class="w-full flex items-center justify-end">
                <b-input
                  :value="inventoryDraftQtyById[props.row.id] != null ? String(inventoryDraftQtyById[props.row.id]) : ''"
                  type="number"
                  min="0"
                  size="is-small"
                  :max="props.row.outbound_quantity != null ? props.row.outbound_quantity : undefined"
                  :disabled="isRowExhausted(props.row)"
                  @input="(val) => onInventoryRowQtyInput(props.row, val)"
                />
              </div>
            </b-table-column>
            <b-table-column field="actions" :label="$t('actions')" centered width="110" v-slot="props">
              <div class="w-full flex items-center justify-center">
                <b-button
                  type="is-primary"
                  size="is-small"
                  :disabled="!canAddInventoryRow(props.row)"
                  @click="() => onAddInventoryRow(props.row)"
                >
                  {{ $t('add') }}
                </b-button>
              </div>
            </b-table-column>
          </b-table>

          <p v-if="!isInventoryListLoading && inventoryListRows.length === 0" class="text-sm text-gray-600 m-0 py-2">
            {{ $t('no_results') }}
          </p>
        </div>

        <div class="mt-3 max-w-full overflow-x-auto warehouse-table-responsive">
          <b-table
            :data="form.items"
            striped
            bordered
            mobile-cards
            icon-pack="fas"
          >
            <b-table-column field="delivery_number" :label="$t('delivery_number')" width="130" v-slot="props">
              {{ props.row.delivery_number || '-' }}
            </b-table-column>
            <b-table-column field="unique_material_id" :label="$t('unique_material_id')" v-slot="props">
              {{ props.row.unique_material_id || '-' }}
            </b-table-column>
            <b-table-column field="description" :label="$t('description')" v-slot="props">
              {{ props.row.description || '-' }}
            </b-table-column>
            <b-table-column field="provider" :label="$t('provider')" v-slot="props">
              {{ props.row.provider || '-' }}
            </b-table-column>
            <b-table-column field="unit" :label="$t('unit')" width="90" v-slot="props">
              {{ props.row.unit || '-' }}
            </b-table-column>
            <b-table-column field="drawing_number" :label="$t('drawing_number')" v-slot="props">
              {{ props.row.drawing_number || '-' }}
            </b-table-column>
            <b-table-column field="tag_kks" :label="$t('tag_kks')" v-slot="props">
              {{ props.row.tag_kks || '-' }}
            </b-table-column>
            <b-table-column field="quantity" :label="$t('quantity')" numeric width="120" v-slot="props">
              {{ props.row.quantity != null ? props.row.quantity : '-' }}
            </b-table-column>
            <b-table-column field="actions" :label="$t('actions')" centered width="110" v-slot="props">
              <b-button type="is-danger" size="is-small" outlined @click="onRemoveMaterialLine(props.index)">
                {{ $t('delete') }}
              </b-button>
            </b-table-column>
          </b-table>
        </div>
      </div>
      <div class="mt-3 flex items-center justify-end gap-2">
        <b-button size="is-small" @click="onCancelCreateReturn">
          {{ $t('cancel') }}
        </b-button>
        <b-button
          type="is-primary"
          size="is-small"
          :loading="isSavingReturn"
          @click="onSaveCreateReturn"
        >
          {{ $t('save') }}
        </b-button>
      </div>
    </div>

    <div v-else class="max-w-full overflow-x-auto">
      <b-collapse
        class="card filters-collapse mb-3"
        v-model="showListFilters"
      >
        <template #trigger>
          <div class="card-header filters-trigger">
            <p class="card-header-title">
              {{ $t('filters') }}
            </p>
            <a class="card-header-icon">
              <b-icon pack="fa" v-show="!showListFilters" icon="plus-circle" />
              <b-icon pack="fa" v-show="showListFilters" icon="minus-circle" />
            </a>
          </div>
        </template>

        <div class="panel" style="border-width: 0">
          <div class="grid grid-cols-1 md:grid-cols-4 gap-3">
            <b-field :label="$t('subcontractor')">
              <b-select
                v-model="listFilters.partner_id"
                expanded
                :loading="isPartnersLoading"
                :disabled="isPartnersLoading"
                :placeholder="$t('subcontractor')"
                @input="onListFiltersChange"
              >
                <option :value="null">{{ $t('all') }}</option>
                <option
                  v-for="partner in subcontractorOptions"
                  :key="`lf-pt-${partner.id}`"
                  :value="partner.id"
                >
                  {{ partner.name || partner.code || `#${partner.id}` }}
                </option>
              </b-select>
            </b-field>

            <b-field :label="$t('date_start')">
              <b-input
                v-model="listFilters.return_date_from"
                type="date"
                @input="onListFiltersChange"
              />
            </b-field>

            <b-field :label="$t('date_end')">
              <b-input
                v-model="listFilters.return_date_to"
                type="date"
                @input="onListFiltersChange"
              />
            </b-field>

            <b-field :label="$t('search')">
              <b-input
                v-model.trim="listFilters.search"
                :placeholder="$t('search')"
                @input="onListFiltersChangeDebounced"
              />
            </b-field>
          </div>
        </div>
      </b-collapse>

      <b-table
      :data="rows"
      :loading="isLoading"
      detailed
      detail-key="id"
      :show-detail-icon="true"
      :has-detailed-visible="() => true"
      paginated
      :per-page="25"
      striped
      bordered
      mobile-cards
      icon-pack="fas"
    >
      <b-table-column
        field="return_number"
        :label="$t('return_number')"
        width="150"
        v-slot="props"
      >
        {{ props.row.return_number || '-' }}
      </b-table-column>

      <b-table-column
        field="return_date"
        :label="$t('return_date')"
        width="140"
        v-slot="props"
      >
        {{ formatReturnDate(props.row.return_date) }}
      </b-table-column>

      <b-table-column field="description" :label="$t('description')" v-slot="props">
        {{ props.row.description || '-' }}
      </b-table-column>

      <b-table-column
        field="subcontractor"
        :label="$t('subcontractor')"
        width="160"
        v-slot="props"
      >
        {{ props.row.subcontractor || '-' }}
      </b-table-column>

      <b-table-column
        field="returned_by"
        :label="$t('returned_by')"
        width="160"
        v-slot="props"
      >
        {{ props.row.returned_by || '-' }}
      </b-table-column>

      <b-table-column
        field="materials_count"
        :label="$t('warehouse_outbound_elements_count')"
        width="160"
        numeric
        v-slot="props"
      >
        {{ props.row.materials_count != null ? props.row.materials_count : '-' }}
      </b-table-column>

      <b-table-column
        field="actions"
        :label="$t('actions')"
        centered
        width="120"
        v-slot="props"
      >
        <div class="w-full h-full flex items-center justify-center gap-3">
          <b-tooltip :label="$t('edit')" position="is-top">
            <div
              role="button"
              tabindex="0"
              class="cursor-pointer text-gray-600"
              @click="onEditReturnRow(props.row)"
              @keydown.enter.prevent="onEditReturnRow(props.row)"
            >
              <b-icon pack="fas" icon="pencil-alt" />
            </div>
          </b-tooltip>
          <b-tooltip :label="$t('export')" position="is-top">
            <div
              role="button"
              tabindex="0"
              class="cursor-pointer text-gray-600"
              :class="{ 'opacity-50 pointer-events-none': isExporting }"
              @click="onExportIdmReport(props.row)"
              @keydown.enter.prevent="onExportIdmReport(props.row)"
            >
              <b-icon pack="fas" icon="file-alt" />
            </div>
          </b-tooltip>
          <b-tooltip :label="$t('delete')" position="is-top" type="is-danger">
            <div
              role="button"
              tabindex="0"
              class="cursor-pointer text-red-600"
              :class="{ 'opacity-50 pointer-events-none': deletingReturnId === props.row.id }"
              @click="onDeleteReturn(props.row)"
              @keydown.enter.prevent="onDeleteReturn(props.row)"
            >
              <b-icon pack="fas" icon="trash-alt" />
            </div>
          </b-tooltip>
        </div>
      </b-table-column>

      <template #detail="props">
        <div class="pl-5 pr-2 pb-4 bg-gray-50">
          <h4 class="text-sm font-semibold text-blue-900 mb-2">
            {{ $t('line_items') }}
          </h4>
          <b-table
            v-if="returnDetailRows(props.row).length"
            :data="returnDetailRows(props.row)"
            striped
            bordered
            narrowed
            mobile-cards
            icon-pack="fas"
          >
            <b-table-column field="unique_material_id" :label="$t('unique_material_id')" v-slot="line">
              {{ line.row.unique_material_id || '-' }}
            </b-table-column>
            <b-table-column field="description" :label="$t('description')" v-slot="line">
              {{ line.row.description || '-' }}
            </b-table-column>
            <b-table-column field="provider" :label="$t('provider')" v-slot="line">
              {{ line.row.provider || '-' }}
            </b-table-column>
            <b-table-column field="unit" :label="$t('unit')" width="110" v-slot="line">
              {{ line.row.unit || '-' }}
            </b-table-column>
            <b-table-column field="idm_report" :label="$t('idm_report')" width="150" v-slot="line">
              {{ line.row.idm_report || '-' }}
            </b-table-column>
            <b-table-column field="quantity" :label="$t('quantity')" numeric width="100" v-slot="line">
              {{ line.row.quantity != null ? line.row.quantity : '-' }}
            </b-table-column>
          </b-table>
          <p v-else class="text-sm text-gray-600 m-0 py-2">
            {{ $t('no_results') }}
          </p>
        </div>
      </template>
      </b-table>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import WarehouseCatalogAutocomplete from './warehouse-catalog-autocomplete.vue'

export default {
  name: 'warehouse-returns',
  components: {
    WarehouseCatalogAutocomplete,
  },
  props: {
    projectId: {
      type: [String, Number],
      required: true,
    },
  },
  data: () => ({
    isLoading: false,
    isSavingReturn: false,
    isExporting: false,
    isCreateMode: false,
    /** id de la devolución siendo editada; null = modo creación */
    editingReturnId: null,
    deletingReturnId: null,
    rows: [],
    showListFilters: false,
    listFilters: {
      partner_id: null,
      return_date_from: '',
      return_date_to: '',
      search: '',
    },
    listFiltersDebounce: null,
    materialOptions: [],
    providerOptions: [],
    unitOptions: [],
    subcontractorOptions: [],
    isCatalogsLoading: false,
    isPartnersLoading: false,
    form: {
      return_number: '',
      return_date: '',
      returned_by: '',
      description: '',
      items: [],
    },
    inventoryFilters: {
      partner_id: null,
      warehouse_material_id: null,
      warehouse_provider_id: null,
      unit_id: null,
      search: '',
    },
    inventoryListRows: [],
    isInventoryListLoading: false,
    inventoryListDebounce: null,
    /** cantidad borrador por id de inventario */
    inventoryDraftQtyById: {},
  }),
  computed: {
    ...mapGetters({
      company: 'app/company',
    }),
    normalizedProjectId() {
      return Number(this.projectId) || null
    },
    isEditingMode() {
      return this.editingReturnId != null
    },
    totalDraftMaterialsQuantity() {
      if (!Array.isArray(this.form.items)) return 0
      return this.form.items.reduce((sum, row) => {
        const q = row && row.quantity != null ? Number(row.quantity) : 0
        return sum + (Number.isFinite(q) && q > 0 ? Math.floor(q) : 0)
      }, 0)
    },
    normalizedWarehouseMaterialId() {
      const v = this.inventoryFilters && this.inventoryFilters.warehouse_material_id
      const n = v != null && v !== '' ? Number(v) : null
      return Number.isFinite(n) && n > 0 ? n : null
    },
    normalizedWarehouseProviderId() {
      const v = this.inventoryFilters && this.inventoryFilters.warehouse_provider_id
      const n = v != null && v !== '' ? Number(v) : null
      return Number.isFinite(n) && n > 0 ? n : null
    },
    normalizedUnitId() {
      const v = this.inventoryFilters && this.inventoryFilters.unit_id
      const n = v != null && v !== '' ? Number(v) : null
      return Number.isFinite(n) && n > 0 ? n : null
    },
    normalizedPartnerId() {
      const v = this.inventoryFilters && this.inventoryFilters.partner_id
      const n = v != null && v !== '' ? Number(v) : null
      return Number.isFinite(n) && n > 0 ? n : null
    },
    /**
     * Los demás filtros se habilitan en cuanto hay una subcontrata seleccionada;
     * el listado de items abarca TODAS sus entregas (multi-outbound).
     */
    isOtherFiltersDisabled() {
      return !this.normalizedPartnerId || this.isCatalogsLoading
    },
  },
  created() {
    this.loadRows()
    this.loadCatalogs()
    this.loadSubcontractors()
  },
  watch: {
    projectId() {
      this.loadRows()
      this.loadCatalogs()
      this.loadSubcontractors()
    },
  },
  methods: {
    formatMaterialOption(m) {
      if (!m) return ''
      return `${m.code || '-'} - ${m.description || '-'}`
    },
    formatProviderOption(p) {
      if (!p) return ''
      return (p.code ? `${p.code} - ` : '') + (p.name || '-')
    },
    extractArrayFromResponse(data, key) {
      if (Array.isArray(data && data[key])) return data[key]
      if (Array.isArray(data && data.data && data.data[key])) return data.data[key]
      return []
    },
    async loadCatalogs() {
      if (!this.normalizedProjectId) {
        this.materialOptions = []
        this.providerOptions = []
        this.unitOptions = []
        return
      }
      this.isCatalogsLoading = true
      try {
        const companyId = this.company && this.company.id ? Number(this.company.id) : null
        const [materialsRes, providersRes, unitsRes] = await Promise.all([
          axios.get(`/api/v2/project/${this.normalizedProjectId}/warehouse/materials`, {
            params: { per_page: 100, page: 1, order_field: 'code', order_dir: 'asc' },
          }),
          axios.get(`/api/v2/project/${this.normalizedProjectId}/warehouse/providers`, {
            params: { per_page: 100, page: 1, order_field: 'name', order_dir: 'asc' },
          }),
          companyId
            ? axios.get(`/api/v2/company/${companyId}/measurement-units`, { params: { per_page: 300 } })
            : Promise.resolve({ data: {} }),
        ])

        this.materialOptions = this.extractArrayFromResponse(materialsRes.data, 'materials')
        this.providerOptions = this.extractArrayFromResponse(providersRes.data, 'providers')
        this.unitOptions = this.extractArrayFromResponse(unitsRes.data, 'units')
      } catch (e) {
        this.materialOptions = []
        this.providerOptions = []
        this.unitOptions = []
      } finally {
        this.isCatalogsLoading = false
      }
    },
    async loadSubcontractors() {
      if (!this.normalizedProjectId) {
        this.subcontractorOptions = []
        return
      }
      this.isPartnersLoading = true
      try {
        const { data } = await axios.get(`/api/v2/project/${this.normalizedProjectId}/partners`, {
          params: { per_page: 100, page: 1 },
        })
        const partners = (data && Array.isArray(data.data)) ? data.data : []
        this.subcontractorOptions = partners
      } catch (e) {
        this.subcontractorOptions = []
      } finally {
        this.isPartnersLoading = false
      }
    },
    async loadRows() {
      if (!this.normalizedProjectId) {
        this.rows = []
        return
      }
      this.isLoading = true
      try {
        const { data } = await axios.get(`/api/v2/project/${this.normalizedProjectId}/warehouse/returns`, {
          params: {
            per_page: 50,
            page: 1,
            order_field: 'id',
            order_dir: 'desc',
            ...this.listFilterParams(),
          },
        })
        this.rows = (data && data.success && Array.isArray(data.returns)) ? data.returns : []
      } catch (e) {
        this.rows = []
      } finally {
        this.isLoading = false
      }
    },
    listFilterParams() {
      const f = this.listFilters || {}
      const search = f.search ? String(f.search).trim() : ''
      const partnerId = f.partner_id != null && f.partner_id !== '' ? Number(f.partner_id) : null
      return {
        partner_id: Number.isFinite(partnerId) && partnerId > 0 ? partnerId : undefined,
        return_date_from: f.return_date_from || undefined,
        return_date_to: f.return_date_to || undefined,
        search: search !== '' ? search : undefined,
      }
    },
    onListFiltersChange() {
      this.loadRows()
    },
    onListFiltersChangeDebounced() {
      if (this.listFiltersDebounce) clearTimeout(this.listFiltersDebounce)
      this.listFiltersDebounce = setTimeout(() => {
        this.loadRows()
      }, 350)
    },
    formatReturnDate(value) {
      if (value == null || value === '') return '-'
      if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
        const d = new Date(value)
        if (!Number.isNaN(d.getTime())) {
          return d.toLocaleDateString('es-ES', {
            day: 'numeric',
            month: 'numeric',
            year: 'numeric',
          })
        }
      }
      return String(value)
    },
    returnDetailRows(row) {
      if (!row || !Array.isArray(row.items)) return []
      return row.items
    },
    onLoadReturn() {
      this.isCreateMode = true
    },
    onFiltersChange() {
      this.loadInventoryList()
    },
    onFiltersChangeDebounced() {
      if (this.inventoryListDebounce) clearTimeout(this.inventoryListDebounce)
      this.inventoryListDebounce = setTimeout(() => {
        this.loadInventoryList()
      }, 220)
    },
    /**
     * Al cambiar la subcontrata: limpiamos los demás filtros y cargamos el listado
     * de items de TODAS las entregas (multi-outbound) de esa subcontrata.
     */
    async onPartnerChange() {
      this.inventoryFilters.warehouse_material_id = null
      this.inventoryFilters.warehouse_provider_id = null
      this.inventoryFilters.unit_id = null
      this.inventoryFilters.search = ''
      this.inventoryListRows = []
      this.inventoryDraftQtyById = {}

      if (!this.normalizedPartnerId) {
        return
      }

      await this.loadInventoryList()
    },
    async loadInventoryList() {
      if (!this.normalizedProjectId || !this.normalizedPartnerId) {
        this.inventoryListRows = []
        return
      }
      const search = this.inventoryFilters.search ? String(this.inventoryFilters.search).trim() : ''
      this.isInventoryListLoading = true
      try {
        const { data } = await axios.get(`/api/v2/project/${this.normalizedProjectId}/warehouse/outbound-items`, {
          params: {
            per_page: 100,
            page: 1,
            order_field: 'id',
            order_dir: 'desc',
            partner_id: this.normalizedPartnerId,
            warehouse_material_id: this.normalizedWarehouseMaterialId || undefined,
            warehouse_provider_id: this.normalizedWarehouseProviderId || undefined,
            unit_id: this.normalizedUnitId || undefined,
            description: search !== '' ? search : undefined,
          },
        })
        const rows = (data && data.success && Array.isArray(data.outbound_items)) ? data.outbound_items : []
        this.inventoryListRows = rows
      } catch (e) {
        this.inventoryListRows = []
      } finally {
        this.isInventoryListLoading = false
      }
    },
    inventoryRowClass(row) {
      return this.isRowExhausted(row) ? 'warehouse-returns-row-exhausted' : ''
    },
    // Cantidad ya añadida al borrador (form.items) para una línea de salida concreta.
    addedQtyForRow(row) {
      if (!row || row.id == null) return 0
      const id = Number(row.id)
      const line = (this.form.items || []).find((it) => it && Number(it.outbound_item_id) === id)
      const q = line ? Number(line.quantity) : 0
      return Number.isFinite(q) && q > 0 ? q : 0
    },
    // Cantidad "efectiva" que se descontará de la salida: el draft manda porque al pulsar Añadir
    // reemplaza cualquier línea ya presente en el borrador; si no hay draft, cae en lo ya añadido.
    effectiveQtyForRow(row) {
      if (!row || row.id == null) return 0
      const draft = this.inventoryDraftQtyById[Number(row.id)]
      if (draft != null && draft !== '') {
        const n = Number(draft)
        return Number.isFinite(n) && n > 0 ? n : 0
      }
      return this.addedQtyForRow(row)
    },
    pendingForRow(row) {
      const max = row && row.outbound_quantity != null ? Number(row.outbound_quantity) : 0
      return Math.max(0, (Number.isFinite(max) ? max : 0) - this.effectiveQtyForRow(row))
    },
    isRowExhausted(row) {
      const max = row && row.outbound_quantity != null ? Number(row.outbound_quantity) : 0
      return this.addedQtyForRow(row) >= (Number.isFinite(max) ? max : 0)
    },
    pendingCellClass(row) {
      const effective = this.effectiveQtyForRow(row)
      if (effective === 0) return 'has-text-grey-light'
      const max = row && row.outbound_quantity != null ? Number(row.outbound_quantity) : 0
      const draft = this.inventoryDraftQtyById[Number(row.id)]
      const draftN = draft != null && draft !== '' ? Number(draft) : null
      if (draftN != null && Number.isFinite(draftN) && draftN > max) return 'has-text-danger has-text-weight-bold'
      return 'has-text-success has-text-weight-bold'
    },
    onInventoryRowQtyInput(row, raw) {
      const rowId = row && row.id != null ? Number(row.id) : null
      if (!Number.isFinite(rowId)) return
      if (raw === '' || raw == null) {
        this.inventoryDraftQtyById = { ...this.inventoryDraftQtyById, [rowId]: null }
        return
      }
      let n = Number(raw)
      if (!Number.isFinite(n)) {
        this.inventoryDraftQtyById = { ...this.inventoryDraftQtyById, [rowId]: null }
        return
      }
      if (n < 0) n = 0
      const max = row && row.outbound_quantity != null ? Number(row.outbound_quantity) : null
      if (max != null && Number.isFinite(max) && n > max) n = max
      this.inventoryDraftQtyById = { ...this.inventoryDraftQtyById, [rowId]: n }
    },
    canAddInventoryRow(row) {
      if (!row || row.id == null) return false
      const rowId = Number(row.id)
      if (!Number.isFinite(rowId)) return false
      const invId = row.warehouse_inventory_id != null ? Number(row.warehouse_inventory_id) : null
      if (!Number.isFinite(invId)) return false
      const qty = this.inventoryDraftQtyById[rowId]
      const q = qty != null ? Number(qty) : NaN
      if (!Number.isFinite(q) || q < 1) return false
      const available = row.outbound_quantity != null ? Number(row.outbound_quantity) : null
      if (available != null && Number.isFinite(available) && q > available) return false
      return true
    },
    onAddInventoryRow(row) {
      if (!this.canAddInventoryRow(row)) {
        this.$toast.open({
          message: this.$t('enter_valid_quantity_int_min_1'),
          type: 'is-warning',
          position: 'is-top-right',
        })
        return
      }
      const outboundItemId = Number(row.id)
      const invId = Number(row.warehouse_inventory_id)
      const quantity = Math.floor(Number(this.inventoryDraftQtyById[outboundItemId]))
      const line = {
        // outbound_item_id identifica la LÍNEA de salida concreta de la que se devuelve.
        // Permite tener varias entradas con el mismo inventory_id provenientes de entregas
        // distintas — el backend luego agrega por inventory_id al persistir.
        outbound_item_id: outboundItemId,
        delivery_number: row.delivery_number || '',
        inventory_id: invId,
        unique_material_id: row.inventory && row.inventory.unique_material_id ? String(row.inventory.unique_material_id).trim() : '',
        description: row.inventory && row.inventory.description ? String(row.inventory.description).trim() : '',
        provider: row.inventory && row.inventory.provider ? String(row.inventory.provider).trim() : '',
        unit: row.inventory && row.inventory.unit ? String(row.inventory.unit).trim() : '',
        drawing_number: row.inventory && row.inventory.drawing_number ? String(row.inventory.drawing_number).trim() : '',
        tag_kks: row.inventory && row.inventory.tag_kks ? String(row.inventory.tag_kks).trim() : '',
        quantity,
      }
      const items = Array.isArray(this.form.items) ? [...this.form.items] : []
      const existingIdx = items.findIndex((it) => it && Number(it.outbound_item_id) === outboundItemId)
      if (existingIdx !== -1) {
        items[existingIdx] = line
        this.form.items = items
      } else {
        this.form.items = [...items, line]
      }
      this.inventoryDraftQtyById = { ...this.inventoryDraftQtyById, [outboundItemId]: null }
    },
    onRemoveMaterialLine(index) {
      if (!Array.isArray(this.form.items)) return
      this.form.items = this.form.items.filter((_, i) => i !== index)
    },
    resetForm() {
      this.form = {
        return_number: '',
        return_date: '',
        returned_by: '',
        description: '',
        items: [],
      }
      this.inventoryFilters = {
        partner_id: null,
        warehouse_material_id: null,
        warehouse_provider_id: null,
        unit_id: null,
        search: '',
      }
      this.inventoryListRows = []
      this.inventoryDraftQtyById = {}
      this.editingReturnId = null
    },
    onCancelCreateReturn() {
      this.resetForm()
      this.isCreateMode = false
    },
    async onEditReturnRow(row) {
      if (!row || row.id == null) return
      this.resetForm()
      this.editingReturnId = Number(row.id)
      this.form = {
        return_number: row.return_number ? String(row.return_number) : '',
        return_date: row.return_date ? String(row.return_date).slice(0, 10) : '',
        returned_by: row.returned_by ? String(row.returned_by) : '',
        description: row.description ? String(row.description) : '',
        items: Array.isArray(row.items)
          ? row.items
            .filter((it) => it && it.warehouse_inventory_id != null)
            .map((it) => ({
              inventory_id: Number(it.warehouse_inventory_id),
              unique_material_id: it.unique_material_id || '',
              description: it.description || '',
              provider: it.provider || '',
              unit: it.unit || '',
              drawing_number: it.drawing_number || '',
              tag_kks: it.tag_kks || '',
              quantity: Number(it.quantity) || 0,
            }))
          : [],
      }
      this.inventoryFilters.partner_id = row.partner_id != null ? Number(row.partner_id) : null
      this.isCreateMode = true
      if (this.inventoryFilters.partner_id) {
        await this.loadInventoryList()
      }
    },
    firstValidationMessage(errorData) {
      const err = errorData && errorData.errors
      if (err && typeof err === 'object') {
        const firstKey = Object.keys(err)[0]
        if (firstKey && Array.isArray(err[firstKey]) && err[firstKey].length) {
          return err[firstKey][0]
        }
      }
      if (errorData && errorData.message) {
        return errorData.message
      }
      return null
    },
    async onSaveCreateReturn() {
      if (!this.normalizedProjectId) return
      if (!this.normalizedPartnerId) {
        this.$toast.open({
          message: this.$t('select_subcontractor'),
          type: 'is-warning',
          position: 'is-top-right',
        })
        return
      }
      if (!this.form.return_date) {
        this.$toast.open({
          message: this.$t('return_date'),
          type: 'is-warning',
          position: 'is-top-right',
        })
        return
      }
      if (!Array.isArray(this.form.items) || this.form.items.length === 0) {
        this.$toast.open({
          message: this.$t('add_at_least_one_line'),
          type: 'is-warning',
          position: 'is-top-right',
        })
        return
      }

      const payload = {
        description: this.form.description ? String(this.form.description).trim() : '',
        partner_id: Number(this.normalizedPartnerId),
        returned_by: this.form.returned_by ? String(this.form.returned_by).trim() : '',
        return_date: this.form.return_date,
        items: [],
      }

      for (const row of this.form.items) {
        if (!row || !row.inventory_id) continue
        const q = Number(row.quantity)
        if (!Number.isFinite(q) || q < 1) {
          this.$toast.open({
            message: this.$t('line_quantity_min_1'),
            type: 'is-warning',
            position: 'is-top-right',
          })
          return
        }
        payload.items.push({
          warehouse_inventory_id: Number(row.inventory_id),
          quantity: q,
        })
      }

      if (payload.items.length === 0) {
        this.$toast.open({
          message: this.$t('add_at_least_one_line'),
          type: 'is-warning',
          position: 'is-top-right',
        })
        return
      }

      this.isSavingReturn = true
      try {
        const isEditing = this.editingReturnId != null
        const url = isEditing
          ? `/api/v2/project/${this.normalizedProjectId}/warehouse/returns/${this.editingReturnId}`
          : `/api/v2/project/${this.normalizedProjectId}/warehouse/returns`
        const { data } = isEditing
          ? await axios.put(url, payload)
          : await axios.post(url, payload)
        if (data && data.success) {
          this.$toast.open({
            message: this.$t('save_successful'),
            type: 'is-success',
            position: 'is-top-right',
          })
          this.resetForm()
          this.isCreateMode = false
          this.loadRows()
          return
        }
        const msg = this.firstValidationMessage(data) || this.$t('error_saving')
        this.$toast.open({
          message: msg,
          type: 'is-danger',
          position: 'is-top-right',
        })
      } catch (e) {
        const resp = e.response && e.response.data
        const msg = this.firstValidationMessage(resp) || (e.message ? String(e.message) : '') || this.$t('error_saving')
        console.error('[warehouse-returns] save return failed', { status: e.response && e.response.status, response: resp, error: e })
        this.$toast.open({
          message: msg,
          type: 'is-danger',
          position: 'is-top-right',
        })
      } finally {
        this.isSavingReturn = false
      }
    },
    onDeleteReturn(row) {
      if (!row || row.id == null) return
      const returnId = Number(row.id)
      const label = row.return_number ? String(row.return_number) : String(returnId)
      this.$buefy.dialog.confirm({
        title: this.$t('delete'),
        message: this.$t('delete_return_confirm_text', [label]),
        confirmText: this.$t('delete'),
        cancelText: this.$t('cancel'),
        type: 'is-danger',
        hasIcon: true,
        onConfirm: async () => {
          this.deletingReturnId = returnId
          try {
            const { data } = await axios.delete(
              `/api/v2/project/${this.normalizedProjectId}/warehouse/returns/${returnId}`
            )
            if (data && data.success) {
              this.$toast.open({
                message: this.$t('deleted_successfully'),
                type: 'is-success',
                position: 'is-top-right',
              })
              this.loadRows()
            } else {
              const msg = this.firstValidationMessage(data) || this.$t('error')
              this.$toast.open({ message: msg, type: 'is-danger', position: 'is-top-right' })
            }
          } catch (e) {
            const resp = e.response && e.response.data
            const msg = this.firstValidationMessage(resp) || (e.message ? String(e.message) : '') || this.$t('error')
            console.error('[warehouse-returns] delete return failed', { status: e.response && e.response.status, response: resp, error: e })
            this.$toast.open({ message: msg, type: 'is-danger', position: 'is-top-right' })
          } finally {
            this.deletingReturnId = null
          }
        },
      })
    },
    onExport() {
      return this.onExportIdmReport()
    },
    async onExportIdmReport(row = null) {
      if (!this.normalizedProjectId) return
      const returnId = row && row.id != null ? Number(row.id) : null
      const isSingle = Number.isFinite(returnId) && returnId >= 1
      const filters = { project_id: this.normalizedProjectId }
      if (isSingle) filters.warehouse_return_id = returnId

      this.isExporting = true
      try {
        const res = await axios.post(
          '/api/v2/export/warehouse-return-report',
          { filters },
          { responseType: 'blob' }
        )
        const contentDisposition =
          (res && res.headers && (res.headers['content-disposition'] || res.headers['Content-Disposition'])) || ''
        const match = /filename\*?=(?:UTF-8''|")?([^;"\n]+)/i.exec(contentDisposition)
        const fallbackName = 'Informe_IDM_Devolucion.xlsx'
        const filename =
          (match && match[1] ? decodeURIComponent(match[1].replace(/"/g, '').trim()) : '') || fallbackName

        const blob = new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        try {
          const msg = (e && e.response && e.response.data && e.response.data.message) || (e && e.message) || this.$t('error')
          if (this.$buefy && this.$buefy.toast && this.$buefy.toast.open) {
            this.$buefy.toast.open({ message: msg, type: 'is-danger' })
          }
        } catch (_) {
          // ignore toast rendering errors
        }
        console.error(e)
      } finally {
        this.isExporting = false
      }
    },
  },
}
</script>

<style scoped>
.warehouse-table-responsive ::v-deep table {
  width: 100%;
  table-layout: fixed;
}

.warehouse-table-responsive ::v-deep th,
.warehouse-table-responsive ::v-deep td {
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  vertical-align: middle;
}

.warehouse-table-responsive ::v-deep th .th-wrap {
  display: block;
  max-width: 100%;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.warehouse-table-responsive ::v-deep th .th-wrap,
.warehouse-table-responsive ::v-deep td {
  min-width: 0;
}

.warehouse-table-responsive ::v-deep .control {
  margin-bottom: 0;
}
/* Fila con toda la cantidad de salida ya incluida en el borrador de devolución. */
::v-deep .warehouse-returns-row-exhausted {
  opacity: 0.55;
}
</style>
